/**
 * AdminTransactions — the platform-wide ticket transaction ledger.
 * Every order across every event: search, filters, fees, sources, pagination.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Loader2, Receipt, ArrowLeft, Search, DollarSign, Percent, Ticket, ShoppingCart, ChevronLeft, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const usd = (c) => `$${(c / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

const SOURCE_OPTIONS = [
  { value: '', label: 'All sources' },
  { value: 'event_page', label: 'Event page' },
  { value: 'widget', label: 'Widget' },
  { value: 'venue_experiences', label: 'Venue experiences' },
  { value: 'missed_upgrade_email', label: 'Upgrade email' },
];
const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'completed', label: 'Completed' },
  { value: 'pending', label: 'Pending' },
  { value: 'refunded', label: 'Refunded' },
];
const DAYS_OPTIONS = [
  { value: 0, label: 'All time' },
  { value: 7, label: 'Last 7d' },
  { value: 30, label: 'Last 30d' },
  { value: 90, label: 'Last 90d' },
];
const SOURCE_BADGE = {
  widget: { label: 'WIDGET', cls: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30' },
  venue_experiences: { label: 'VENUE EXP', cls: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
  missed_upgrade_email: { label: 'UPGRADE', cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' },
  event_page: { label: 'EVENT PAGE', cls: 'bg-slate-500/20 text-slate-300 border-slate-500/30' },
};
const STATUS_CLS = {
  completed: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  pending: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  refunded: 'bg-red-500/15 text-red-400 border-red-500/30',
};

const PAGE_SIZE = 25;

export default function AdminTransactions() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [source, setSource] = useState('');
  const [status, setStatus] = useState('');
  const [days, setDays] = useState(0);
  const [page, setPage] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE, skip: page * PAGE_SIZE });
      if (q.trim()) params.set('q', q.trim());
      if (source) params.set('source', source);
      if (status) params.set('status', status);
      if (days) params.set('days', days);
      const res = await axios.get(`${API}/admin/transactions?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch { /* noop */ }
    setLoading(false);
  }, [token, q, source, status, days, page]);

  useEffect(() => { const t = setTimeout(fetchData, q ? 350 : 0); return () => clearTimeout(t); }, [fetchData, q]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const s = data?.summary;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-background pt-20 pb-12" data-testid="admin-transactions-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <button onClick={() => navigate('/admin')} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </button>
        <div className="flex items-center gap-2 mb-6">
          <Receipt className="h-6 w-6 text-emerald-400" />
          <div>
            <h1 className="text-2xl font-bold">Ticket Transactions</h1>
            <p className="text-sm text-muted-foreground">Every order across every event on the platform</p>
          </div>
        </div>

        {s && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            {[
              { icon: DollarSign, label: 'Gross (completed)', value: usd(s.gross_cents), color: 'text-emerald-400', tid: 'txn-sum-gross' },
              { icon: Percent, label: 'Fees earned', value: usd(s.fees_cents), color: 'text-amber-400', tid: 'txn-sum-fees' },
              { icon: Ticket, label: 'Tickets · Add-ons', value: `${s.tickets.toLocaleString()} · ${s.addons.toLocaleString()}`, color: 'text-indigo-400', tid: 'txn-sum-tickets' },
              { icon: ShoppingCart, label: 'Orders', value: s.orders.toLocaleString(), color: 'text-sky-400', tid: 'txn-sum-orders' },
            ].map(c => (
              <Card key={c.label} className="border-border"><CardContent className="p-3.5" data-testid={c.tid}>
                <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground uppercase tracking-wide"><c.icon className={`h-3.5 w-3.5 ${c.color}`} /> {c.label}</div>
                <p className="text-lg font-bold mt-0.5">{c.value}</p>
              </CardContent></Card>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder="Search buyer, email, or order id…" value={q}
              onChange={e => { setQ(e.target.value); setPage(0); }} data-testid="txn-search" />
          </div>
          {[{ v: source, set: setSource, opts: SOURCE_OPTIONS, tid: 'txn-filter-source' },
            { v: status, set: setStatus, opts: STATUS_OPTIONS, tid: 'txn-filter-status' },
            { v: days, set: (x) => setDays(Number(x)), opts: DAYS_OPTIONS, tid: 'txn-filter-days' }].map((f, i) => (
            <select key={i} value={f.v} onChange={e => { f.set(e.target.value); setPage(0); }}
              className="h-10 px-3 rounded-md border border-border bg-background text-sm" data-testid={f.tid}>
              {f.opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          ))}
        </div>

        <Card className="border-border">
          <CardContent className="p-0 overflow-x-auto">
            {loading && !data ? (
              <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-indigo-400" /></div>
            ) : !data || data.transactions.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-12" data-testid="txn-empty">No transactions match these filters.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="text-left px-4 py-2.5 font-semibold">Date</th>
                    <th className="text-left px-3 py-2.5 font-semibold">Buyer</th>
                    <th className="text-left px-3 py-2.5 font-semibold">Event</th>
                    <th className="text-center px-3 py-2.5 font-semibold">Items</th>
                    <th className="text-left px-3 py-2.5 font-semibold">Source</th>
                    <th className="text-left px-3 py-2.5 font-semibold">Status</th>
                    <th className="text-right px-3 py-2.5 font-semibold">Fee</th>
                    <th className="text-right px-4 py-2.5 font-semibold">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.transactions.map(t => {
                    const src = SOURCE_BADGE[t.source] || SOURCE_BADGE.event_page;
                    return (
                      <tr key={t.order_id} className="border-b border-border last:border-0 hover:bg-accent/40 cursor-pointer transition-colors"
                        onClick={() => navigate(`/admin/events/${t.event_id}/transactions`)} data-testid={`txn-${t.order_id}`}>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{t.created_at ? new Date(t.created_at).toLocaleDateString() : '—'}</td>
                        <td className="px-3 py-2.5">
                          <p className="font-medium text-xs truncate max-w-[160px]">{t.buyer_name || '—'}</p>
                          <p className="text-[10px] text-muted-foreground truncate max-w-[160px]">{t.buyer_email}</p>
                        </td>
                        <td className="px-3 py-2.5"><p className="text-xs truncate max-w-[200px]">{t.event_name}</p></td>
                        <td className="px-3 py-2.5 text-center text-xs whitespace-nowrap">{t.tickets}t{t.addons > 0 ? ` +${t.addons}a` : ''}</td>
                        <td className="px-3 py-2.5"><Badge className={`${src.cls} border text-[8px] px-1`}>{src.label}</Badge></td>
                        <td className="px-3 py-2.5"><Badge className={`${STATUS_CLS[t.status] || 'bg-muted'} border text-[8px] px-1 uppercase`}>{t.status}</Badge></td>
                        <td className="px-3 py-2.5 text-right text-xs text-amber-400 whitespace-nowrap">{t.fee_cents > 0 ? usd(t.fee_cents) : '—'}</td>
                        <td className="px-4 py-2.5 text-right font-bold text-xs whitespace-nowrap">{t.total === 0 ? 'Free' : `$${t.total.toFixed(2)}`}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {data && data.total > PAGE_SIZE && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-xs text-muted-foreground">
              {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, data.total)} of {data.total.toLocaleString()}
            </p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="txn-prev">
                <ChevronLeft className="h-4 w-4" /> Prev
              </Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="txn-next">
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
